import { Directive, HostBinding, HostListener, Input } from '@angular/core';
import { debounce } from '../decorators/debounce';
import { AnalyticService, TrackEvent } from '../services/analytic.service';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[track]',
})
export class EventTrackDirective {
  @HostBinding('track')
  @Input()
  track: any | string;

  private event: any;

  constructor(private analytics: AnalyticService) {}

  @HostListener('click', ['$event'])
  trackEvent($event: any) {
    if (this.track) {
      this.event = $event;

      /**
       * Omit inputs, selects, and checkboxes from click events
       * Concats the classes from the element and parent
       */
      const className = `
        ${this.event.target?.className}
        ${this.event.target?.parentElement.className}`;

      if (
        [
          'mat-form',
          'mat-input',
          'mat-input-element',
          'mat-select',
          'mat-radio',
          'mat-checkbox',
          'mat-button-toggle',
        ].filter((c) => className.includes(c)).length > 0
      ) {
        return;
      }

      const track = this.decodeTrackString();

      if (track.object) {
        this.submitEvent({
          object: track.object,
          action: 'Click',
          properties: track.properties,
        });
      }
    }
  }

  @HostListener('input', ['$event'])
  @debounce(2000)
  trackInputChange($event: any) {
    // Check if the track event contains data, and that the input is not hidden
    if (this.track && !$event.target?.className.includes('hidden')) {
      // Decode the string using the provided track value
      const track = this.decodeTrackString();

      if (track.object) {
        this.submitEvent({
          object: track.object,
          action: 'Input',
          properties: {
            ...track.properties,
            value: $event.target.value,
          },
        });
      }
    }
  }

  @HostListener('selectionChange', ['$event'])
  @debounce(500)
  trackSelectChange($event: any) {
    if (this.track) {
      this.event = $event;
      const element = $event.source?._elementRef.nativeElement;

      // Decode the string using the provided track value
      const track = this.decodeTrackString();

      if (track.object) {
        this.submitEvent({
          object: track.object,
          action: 'Select',
          properties: {
            ...track.properties,
            value: $event.value,
            displayValue: element?.innerText.trim(),
          },
        });
      }
    }
  }

  @HostListener('change', ['$event'])
  @debounce(500)
  trackCheckboxChange($event: any) {
    if (this.track) {
      this.event = $event;
      const element = $event.source?._elementRef.nativeElement;

      // Decode the string using the provided track value
      const track = this.decodeTrackString();

      if (track.object) {
        this.submitEvent({
          object: track.object,
          action: 'Select',
          properties: {
            value: $event.value ?? null,
            checked: typeof $event.checked === 'boolean' ? `${$event.checked}` : null,
            displayValue: element?.innerText?.trim() ?? null,
            ...track.properties,
          },
        });
      }
    }
  }

  /**
   * Submits the tracking event to the analytic service
   * @param event
   * @param partners
   */
  private submitEvent({ object, action, properties = null }: TrackEvent) {
    this.analytics.track({ object, action, properties }, this.event);
  }

  /**
   * Converts a string to an object/action and breaks out partner commands
   * Note that additional properties may only be added when track  is provided as an object
   */
  private decodeTrackString() {
    let properties = {};
    let object;

    if (this.track) {
      // @ts-ignore
      const isTrackString = typeof this.track === 'string' ? true : false;
      const trackArray = isTrackString ? this.track.split('|') : this.track;
      const objectAction = trackArray[0];
      const extraProperties = trackArray[2] ? JSON.parse(trackArray[2]?.trim()) : null;
      const objectActionSplit = isTrackString ? objectAction.split(',') : [...this.track];

      object = objectActionSplit[0].trim();
      if (objectActionSplit[1]) {
        properties = {
          type: objectActionSplit[1].trim(),
          ...extraProperties,
        };
      }
    }
    return { object, properties };
  }
}
